import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Database, ShieldAlert } from "lucide-react";

export default function DataQuality() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const flag = sp.get("flag") || "";
  const view = sp.get("view") || "";
  const [cards, setCards] = useState([]);
  const [items, setItems] = useState([]);
  const [coverage, setCoverage] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/dashboard/data-quality${flag ? `?flag=${flag}` : ""}`);
    setCards(data.cards);
    setItems(data.items || []);
  }, [flag]);
  useEffect(() => { load(); }, [load]);

  const loadCoverage = useCallback(async () => {
    if (view !== "incomplete") { setCoverage(null); return; }
    const { data } = await api.get(`/dashboard/renewal-coverage?service_type=&incomplete=1`);
    setCoverage(data);
  }, [view]);
  useEffect(() => { loadCoverage(); }, [loadCoverage]);

  const REASON_LABEL = { expiry: "Expiry Date", cycle: "Renewal Cycle", isp_id: "ISP Account/Circuit ID", amount: "Amount", customer: "Customer" };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-head text-2xl font-extrabold flex items-center gap-2"><Database className="text-orange-600" /> Data Quality Exceptions</h1>
        <p className="text-slate-500 text-sm mt-1">A renewal system is only as good as its data. Fix these to guarantee every active circuit is tracked.</p>
      </div>

      {/* Renewal-control incomplete view */}
      {view === "incomplete" && coverage && (
        <div className="bg-white border border-orange-300 rounded-xl overflow-hidden" data-testid="incomplete-coverage">
          <div className="p-4 bg-orange-50 border-b border-orange-200 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 font-head font-bold text-orange-800"><ShieldAlert size={18} /> {coverage.incomplete} Active Circuits NOT Under Renewal Control</div>
            <div className="text-sm text-slate-600">Coverage: <b>{coverage.coverage_pct}%</b> ({coverage.controlled}/{coverage.active})</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
                <th className="px-4 py-3">Circuit</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">ISP</th>
                <th className="px-4 py-3">Type</th><th className="px-4 py-3">Missing for Renewal Control</th><th className="px-4 py-3 text-right">Fix</th></tr></thead>
              <tbody>
                {(coverage.items || []).map((c) => (
                  <tr key={c.id} className="border-b border-slate-100">
                    <td className="px-4 py-3 font-mono-num text-xs">{c.internal_circuit_id}</td>
                    <td className="px-4 py-3 font-semibold">{c.customer_name}</td>
                    <td className="px-4 py-3">{c.vendor_name}</td>
                    <td className="px-4 py-3">{c.service_type}</td>
                    <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{(c.renewal_control_reasons || []).map((r) => <span key={r} className="text-[10px] px-2 py-0.5 rounded bg-red-100 text-red-700">{REASON_LABEL[r] || r}</span>)}</div></td>
                    <td className="px-4 py-3 text-right"><button onClick={() => nav(`/circuit/${c.id}`)} className="text-blue-700 text-xs font-medium">Open →</button></td>
                  </tr>
                ))}
                {(!coverage.items || coverage.items.length === 0) && <tr><td colSpan={6} className="px-4 py-10 text-center text-emerald-600">🎉 All active circuits are under renewal control.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((c) => (
          <button key={c.key} onClick={() => setSp(flag === c.key ? {} : { flag: c.key })} data-testid={`dq-card-${c.key}`}
            className={`text-left rounded-xl border p-4 transition-all ${flag === c.key ? "border-orange-400 ring-1 ring-orange-300 bg-orange-50" : "bg-white border-slate-200 hover:border-orange-300"}`}>
            <div className="font-head text-3xl font-extrabold text-orange-600">{c.count}</div>
            <div className="text-xs text-slate-600 mt-1 font-medium">{c.label}</div>
          </button>
        ))}
      </div>

      {flag && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="dq-items">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
              <th className="px-4 py-3">Circuit</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">ISP</th>
              <th className="px-4 py-3">Type</th><th className="px-4 py-3">Expiry</th><th className="px-4 py-3">Missing</th></tr></thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-slate-100">
                  <td className="px-4 py-3 font-mono-num text-xs">{c.internal_circuit_id}</td>
                  <td className="px-4 py-3 font-semibold">{c.customer_name}</td>
                  <td className="px-4 py-3">{c.vendor_name}</td>
                  <td className="px-4 py-3">{c.service_type}</td>
                  <td className="px-4 py-3 font-mono-num text-xs">{fmtDate(c.next_action_date)}</td>
                  <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{c.data_quality_flags.map((f) => <span key={f} className="text-[10px] px-2 py-0.5 rounded bg-red-100 text-red-700">{f.replace(/_/g, " ")}</span>)}</div></td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No circuits with this issue.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
